import { useState } from 'react'
import { avatarStyle, dateInputToDate } from '../lib/format.js'
import { Modal, Button, Field, Chip } from '../ds/index.js'

export default function NewTaskModal({ projects, currentProjectId, users, labels, statuses, onClose, onCreate }) {
  const firstOpenStatus = statuses.find((s) => !s.done) || statuses[0] || {}
  const [draft, setDraft] = useState({
    title: '',
    projectId: currentProjectId,
    status: firstOpenStatus.id || 'todo',
    assigneeUid: null,
    due: '',
    labelIds: [],
    phase: null,
  })
  const patch = (p) => setDraft((d) => ({ ...d, ...p }))
  const valid = draft.title.trim().length > 0

  const toggleLabel = (id) =>
    patch({ labelIds: draft.labelIds.includes(id) ? draft.labelIds.filter((x) => x !== id) : [...draft.labelIds, id] })

  const submit = () => {
    if (!valid) return
    onCreate({
      projectId: draft.projectId,
      title: draft.title.trim(),
      description: '',
      assigneeUid: draft.assigneeUid,
      status: draft.status,
      dueDate: dateInputToDate(draft.due),
      labelIds: draft.labelIds,
      phase: draft.phase || null,
    })
  }

  return (
    <Modal
      onClose={onClose}
      title="New Task"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!valid}>Create Task</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <input value={draft.title} onChange={(e) => patch({ title: e.target.value })} autoFocus placeholder="Task name" className="cn-input" style={{ fontSize: 16, padding: '11px 13px' }} />

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div className="tag-label" style={{ color: 'var(--color-muted)', marginBottom: 8 }}>Project</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {projects.map((p) => (
                <Chip key={p.id} as="button" active={p.id === draft.projectId} onClick={() => patch({ projectId: p.id })}>{p.name}</Chip>
              ))}
            </div>
          </div>
          <Field as="select" label="Status" value={draft.status} onChange={(e) => patch({ status: e.target.value })} style={{ width: 'auto' }}>
            {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <div className="tag-label" style={{ color: 'var(--color-muted)', marginBottom: 8 }}>Assignee</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {users.map((u) => {
                const on = u.uid === draft.assigneeUid
                return (
                  <button
                    key={u.uid}
                    onClick={() => patch({ assigneeUid: u.uid })}
                    title={u.name + (u.pending ? ' (invited — not signed in yet)' : '')}
                    style={{ ...avatarStyle(u.color, u.ink, 28), cursor: 'pointer', outline: on ? '2px solid var(--color-gold)' : u.pending ? '1px dashed var(--border-dash)' : '2px solid transparent', outlineOffset: '2px', opacity: on ? 1 : u.pending ? 0.5 : 0.55 }}
                  >
                    {u.initials}
                  </button>
                )
              })}
            </div>
          </div>
          <Field label="Due date" type="date" value={draft.due} onChange={(e) => patch({ due: e.target.value })} style={{ width: 'auto', colorScheme: 'dark', fontFamily: 'var(--font-mono)', fontSize: 12, padding: '7px 10px' }} />
        </div>

        <div>
          <div className="tag-label" style={{ color: 'var(--color-muted)', marginBottom: 8 }}>Labels</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {labels.map((l) => {
              const on = draft.labelIds.includes(l.id)
              return (
                <Chip key={l.id} as="button" active={on} color={l.color} dot onClick={() => toggleLabel(l.id)}>
                  {l.name}
                </Chip>
              )
            })}
          </div>
        </div>

        <Field as="select" label="Phase (30-60-90)" value={draft.phase || ''} onChange={(e) => setDraft((d) => ({ ...d, phase: e.target.value || null }))}>
          <option value="">No phase</option>
          <option value="p1">Phase 1 · 30-day</option>
          <option value="p2">Phase 2 · 60-day</option>
          <option value="p3">Phase 3 · 90-day</option>
        </Field>
      </div>
    </Modal>
  )
}
